/* istanbul ignore file */
import { useEffect, useState } from 'react';
import { candidateTimelineRecords } from 'domain/recruiting/candidate/shared/timeline';
import { READ, UPDATE } from 'domain/shared/core/permissions';
import { Typography, Box } from '@mui/material';
import { Show, useShowContext } from 'framework/show';
import { useAuthorization } from 'framework/authorization';
import useCandidateOfferService, { RECRUITING_OFFER_READ_CALCULATED_AMOUNT_GROSS } from './candidateOfferService';
import OfferCompensationDetailsSection from './OfferCompensationDetailsSection';
import TimelineOfferButtonGroup from './TimelineOfferButtonGroup';
import VisibilityOffOutlined from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import IconButton from '@mui/material/IconButton';
import StatusChip from 'domain/recruiting/candidate/offercompensation/createupdate/StatusChip';
import { ShowDate } from 'framework/ui/ui-builder';
import { DATE_FORMAT } from 'framework/utils';
import { EditTimelineRecordButton } from 'domain/shared/timeline/button';
import BasePersonTimelineRecordLayout from 'domain/shared/timeline/person/PersonTimelineRecord/BasePersonTimelineRecordLayout';
import {
  CANDIDATE_TIMELINE_ICONS,
  CANDIDATE_TIMELINE_MESSAGE_CONTEXT,
  CANDIDATE_TIMELINE_OFFER_STATUSES,
} from 'domain/shared/candidate';

type OfferTimelineRecordLayoutProps = {
  onEdit: () => void;
  actionName: string;
};

const OfferTimelineRecordLayout = ({ onEdit, actionName, ...props }: OfferTimelineRecordLayoutProps) => {
  const { getById, getOfferPermissions } = useCandidateOfferService();

  const [isShown, setIsShown] = useState(false);

  const { isGranted } = useAuthorization();

  const { data: entity } = useShowContext();

  const [canEditStatus, setCanEditStatus] = useState(false);

  const buttons = [<EditTimelineRecordButton permissionToCheck={UPDATE} key="edit" onEdit={onEdit} />];

  useEffect(() => {
    if (isShown) {
      (async () => {
        const permissions = await getOfferPermissions(entity.offerId);
        if (permissions.includes('UPDATE_STATUS')) {
          setCanEditStatus(true);
        }
      })();
    }
  }, [isShown]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <BasePersonTimelineRecordLayout
      permissionToCheck={READ}
      actionName={actionName}
      timelineType={candidateTimelineRecords.OFFER}
      buttons={entity.offerStatus === CANDIDATE_TIMELINE_OFFER_STATUSES.DRAFT ? buttons : []}
      fields={
        <>
          <Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography>Offer was added</Typography>

              {entity.offerStatus === null ? (
                <>
                  <StatusChip status={entity.offerStatus} />
                  <Box p={2.5} />
                </>
              ) : (
                <>
                  {isShown ? (
                    <IconButton onClick={() => setIsShown(false)}>
                      <VisibilityOffOutlined />
                    </IconButton>
                  ) : (
                    <>
                      <StatusChip status={entity.offerStatus} />
                      <IconButton onClick={() => setIsShown(true)}>
                        <VisibilityOutlined />
                      </IconButton>
                    </>
                  )}
                </>
              )}
            </Box>
            {isShown ? (
              <Show getDataMethod={() => getById(entity.offerId)}>
                <Box display="flex" justifyContent="space-between" alignItems="center" pb={2}>
                  <StatusChip status={entity.offerStatus} />
                  {entity.updatedAt && (
                    <Typography color="textSecondary" variant="caption" fontStyle="italic">
                      Modified:
                      <ShowDate dateUTC={entity.getValueBySource('updatedAt')} format={DATE_FORMAT.FULL} />
                    </Typography>
                  )}
                </Box>
                <OfferCompensationDetailsSection
                  canViewCalculatedGrossField={isGranted(RECRUITING_OFFER_READ_CALCULATED_AMOUNT_GROSS)}
                />
                {canEditStatus && <TimelineOfferButtonGroup status={entity.offerStatus} id={entity.offerId} />}
              </Show>
            ) : null}
          </Box>
        </>
      }
      timelineIcons={CANDIDATE_TIMELINE_ICONS}
      messageContext={CANDIDATE_TIMELINE_MESSAGE_CONTEXT}
      {...props}
    />
  );
};

export default OfferTimelineRecordLayout;
